import * as React from 'react';

import { DocumentType } from '@rematter/frontend-lib-api-client';
import { useToast } from '@rematter/ui-web/hooks';
import { useSetAtom } from 'jotai';
import { PDFDocument, PDFPage } from 'pdf-lib';
import printJS from 'print-js';

import { $generateFileAccessURL } from './useFileDownloadUrl';

import { useTranslations } from '~/modules/trade/i18n/useTranslations';
import { useApi } from '~/shared/hooks';
import { createMutationAtomFactory } from '~/shared/providers/api/createMutationAtomFactory';

type InvoiceData = {
  id: string;
  documentNumber: string;
  documentDate: string;
  dueDate: string;
  status: string;
  amount: string;
};

type DocumentRequest = {
  documentType: DocumentType;
  bookingId?: string;
  loadId?: string;
  salesOrderId?: string;
  invoiceId?: string;
  purchaseOrderId?: string;
};

type DocumentApiInput = {
  documentType: DocumentType;
  bookingId?: string;
  loadId?: string;
  salesOrderId?: string;
  purchaseOrderId?: string;
  invoiceNumber?: string;
  invoiceDate?: string;
  invoiceAmount?: string;
  dueDate?: string;
};

const generateDocumentAtom = createMutationAtomFactory(
  'trade.tradeGenerateDocument',
  (params: { documents: DocumentApiInput[] }) => ({
    tradeGenerateDocument: {
      __args: {
        input: {
          documents: params.documents,
        },
      },
      documents: {
        fileId: true,
        documentType: true,
        success: true,
        errorMessage: true,
      },
    },
  }),
  {
    select: (data) =>
      data.tradeGenerateDocument?.documents.map((doc) => ({
        documentType: doc.documentType,
        success: doc.success,
        fileId: doc.fileId,
        errorMessage: doc.errorMessage ?? '',
      })),
  },
);

const $generateDocument = generateDocumentAtom();

type PrintWithBrowserParams = {
  documents: DocumentRequest[];
  action?: 'print' | 'download';
};

type DocumentGenerationResult = {
  documentType: string;
  success: boolean;
  fileId: string;
  errorMessage: string;
};

// Utility function to convert blob to base64
const convertToBase64 = (blob: Blob): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(blob);
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = (error) => reject(error);
  });

// Utility function to download a PDF
const downloadPdf = (blob: Blob, fileName: string) => {
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
};

// Utility function to print a PDF
const printPdf = async (blob: Blob) => {
  const base64Data = await convertToBase64(blob);
  const base64Content = base64Data.split(',')[1];

  printJS({
    printable: base64Content,
    type: 'pdf',
    base64: true,
  });
};

// Utility function to combine multiple PDFs into one
const combinePdfs = async (pdfBlobs: Blob[]): Promise<Blob> => {
  const mergedPdf = await PDFDocument.create();

  for (const pdfBlob of pdfBlobs) {
    const pdfBytes = await pdfBlob.arrayBuffer();
    const pdf = await PDFDocument.load(pdfBytes);
    const copiedPages = await mergedPdf.copyPages(pdf, pdf.getPageIndices());
    copiedPages.forEach((page: PDFPage) => mergedPdf.addPage(page));
  }

  const mergedPdfBytes = await mergedPdf.save();
  const uint8Array = new Uint8Array(mergedPdfBytes);
  return new Blob([uint8Array], { type: 'application/pdf' });
};

export const usePrintWithBrowser = () => {
  const toast = useToast();
  const t = useTranslations();
  const generateDocument = useSetAtom($generateDocument);
  const generateDownloadUrl = useSetAtom($generateFileAccessURL);
  const [isPrinting, setIsPrinting] = React.useState(false);
  const { client } = useApi();

  const print = React.useCallback(
    async (params: PrintWithBrowserParams) => {
      const { action = 'print' } = params;
      setIsPrinting(true);

      try {
        const invoiceDocTypes: DocumentType[] = ['Invoice', 'LoadInvoice'];
        const invoiceIds = params.documents
          .filter((doc) => invoiceDocTypes.includes(doc.documentType))
          .map((doc) => doc.invoiceId)
          .filter((id): id is string => !!id);

        const invoicesByInvoiceId = new Map<string, InvoiceData>();

        if (invoiceIds.length > 0) {
          // Use the API client directly to fetch invoices
          const result = await client('query.sales.invoicesByIds').query({
            invoices: {
              __args: {
                filter: {
                  ids: invoiceIds,
                },
              },
              entries: {
                id: true,
                type: true,
                documentNumber: true,
                documentDate: true,
                dueDate: true,
                status: true,
                notes: true,
                amount: {
                  __scalar: true,
                },
                paymentTerm: true,
                timestamps: {
                  __scalar: true,
                },
              },
            },
          });

          const invoices = result.invoices?.entries ?? [];
          invoices.forEach((invoice) => {
            invoicesByInvoiceId.set(invoice.id, {
              id: invoice.id,
              documentNumber: invoice.documentNumber?.toString() ?? '',
              documentDate: invoice.documentDate,
              dueDate: invoice.dueDate,
              status: invoice.status,
              amount: invoice.amount?.amount.toString() ?? '',
            });
          });
        }

        const documents: DocumentApiInput[] = params.documents.map((doc) => {
          if (invoiceDocTypes.includes(doc.documentType) && doc.invoiceId) {
            const invoice = invoicesByInvoiceId.get(doc.invoiceId);
            return {
              ...doc,
              invoiceId: undefined,
              invoiceNumber: invoice?.documentNumber,
              invoiceDate: invoice?.documentDate,
              dueDate: invoice?.dueDate,
              invoiceAmount: invoice?.amount,
            };
          }
          return doc;
        });

        const results = await generateDocument({
          documents,
        });

        if (results.length === 0) {
          toast.error(t.salesOrderDetail.errors.noDocumentsGenerated);
          return;
        }

        // Check for failed document generation
        const failedResults = results.filter((result) => !result.success);
        if (failedResults.length > 0) {
          const failedMessages = failedResults
            .map(
              (result) =>
                `${result.documentType}: ${result.errorMessage || 'Unknown error'}`,
            )
            .join(', ');
          console.error(failedMessages);
          toast.error(
            t.salesOrderDetail.errors.documentGenerationFailed(failedMessages),
          );
          return;
        }

        // Filter to only successful results
        const successfulResults = results.filter(
          (result): result is DocumentGenerationResult =>
            result.success && !!result.fileId,
        );

        if (successfulResults.length === 0) {
          toast.error(t.salesOrderDetail.errors.noDocumentsGenerated);
          return;
        }

        const downloadResults = await generateDownloadUrl({
          fileIds: successfulResults.map((result) => result.fileId),
        });

        const validUrls =
          downloadResults.filesGenerateFileAccessURL?.results
            ?.filter((result) => !!result && result.url)
            .map((result) => result.url as string) ?? [];

        if (validUrls.length === 0) {
          toast.error(t.salesOrderDetail.errors.noDownloadUrlsGenerated);
          return;
        }

        const urlByFileId = new Map<string, string>();
        downloadResults.filesGenerateFileAccessURL?.results?.forEach(
          (result) => {
            if (result && result.url) {
              urlByFileId.set(result.fileId, result.url);
            }
          },
        );

        if (action === 'download') {
          // For downloads, process each document separately
          const downloadPromises = successfulResults.map(async (result) => {
            const url = urlByFileId.get(result.fileId);
            if (!url) {
              console.error(`No URL found for file ${result.fileId}`);
              return;
            }

            try {
              const response = await fetch(url);
              if (!response.ok) {
                console.error(`Failed to fetch PDF from ${url}`);
                return;
              }

              const blob = await response.blob();

              // Extract filename from content-disposition header or use a default
              const contentDisposition = response.headers.get(
                'content-disposition',
              );
              const fileName =
                contentDisposition?.split('filename=')[1]?.replace(/"/g, '') ??
                `${result.documentType.toLowerCase()}_${result.fileId}.pdf`;

              downloadPdf(blob, fileName);
            } catch (error) {
              console.error(
                `Failed to process document ${result.documentType}:`,
                error,
              );
              toast.error(
                t.salesOrderDetail.errors.failedToProcessDocument(
                  result.documentType,
                ),
              );
            }
          });

          await Promise.all(downloadPromises);
        } else {
          // For printing, combine all PDFs into one document
          const failedDocuments: string[] = [];

          // Fetch all PDFs using Promise.all
          const fetchPromises = successfulResults.map(async (result) => {
            const url = urlByFileId.get(result.fileId);
            if (!url) {
              console.error(`No URL found for file ${result.fileId}`);
              failedDocuments.push(result.documentType);
              return null;
            }

            try {
              const response = await fetch(url);
              if (!response.ok) {
                console.error(`Failed to fetch PDF from ${url}`);
                failedDocuments.push(result.documentType);
                return null;
              }

              const blob = await response.blob();
              return blob;
            } catch (error) {
              console.error(
                `Failed to fetch document ${result.documentType}:`,
                error,
              );
              failedDocuments.push(result.documentType);
              return null;
            }
          });

          const fetchedBlobs = await Promise.all(fetchPromises);
          const validBlobs = fetchedBlobs.filter(
            (blob): blob is Blob => blob !== null,
          );

          if (validBlobs.length === 0) {
            toast.error(t.salesOrderDetail.errors.noDocumentsToPrint);
            return;
          }

          if (failedDocuments.length > 0) {
            toast.warning(
              t.salesOrderDetail.warnings.someDocumentsFailed(
                `Some documents failed to load: ${failedDocuments.join(', ')}`,
              ),
            );
          }

          try {
            // Combine all PDFs into one
            const combinedPdf = await combinePdfs(validBlobs);
            await printPdf(combinedPdf);
          } catch (error) {
            console.error('Failed to combine or print PDFs:', error);
            toast.error(t.salesOrderDetail.errors.failedToPrintCombined);
          }
        }
      } finally {
        setIsPrinting(false);
      }
    },
    [generateDocument, generateDownloadUrl, client, toast, t],
  );

  return { print, isPrinting };
};
